import React from 'react';
import { TimeCheckboxField } from './types';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

interface TimeFieldRowProps {
  id: TimeCheckboxField;
  label: string;
  checked: boolean;
  onToggle: () => void;
  isLoading: boolean;
  hasErrors?: boolean;
  children?: React.ReactNode;
  indexLabel?: string;
  tooltip?: string;
}

/**
 * Row component for a time checkbox field
 * Displays checkbox with label and optional expanded content
 */
export const TimeFieldRow: React.FC<TimeFieldRowProps> = ({
  label,
  checked,
  onToggle,
  isLoading,
  hasErrors = false,
  children,
  indexLabel,
  tooltip
}) => {
  return (
    <div className={`rounded-lg border overflow-visible transition-all duration-150 ${
      checked
        ? hasErrors
          ? 'border-red-200/80 bg-red-50/20'
          : 'border-primary/20 bg-accent/20'
        : 'border-border bg-card'
    }`}>
      <div
        className="flex items-center gap-3 px-3.5 py-2.5 cursor-pointer select-none"
        onClick={onToggle}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="w-3.5 h-3.5 rounded accent-primary flex-shrink-0"
          disabled={isLoading}
          onClick={(e) => e.stopPropagation()}
          aria-label={label}
        />
        <span className={`text-sm transition-colors duration-150 ${
          checked ? 'font-medium text-foreground' : 'text-muted-foreground'
        }`}>
          {label}
        </span>
        {tooltip && (
          <span onClick={(e) => e.stopPropagation()}>
            <InfoTooltip text={tooltip} width="w-80" />
          </span>
        )}
        {indexLabel && (
          <span className="text-[11px] font-mono font-medium text-primary flex-shrink-0">
            {indexLabel}
          </span>
        )}
        {hasErrors && checked && (
          <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
        )}
      </div>

      {checked && children && (
        <div className="px-3.5 pb-3">
          {children}
        </div>
      )}
    </div>
  );
};

export default TimeFieldRow;
